/* eslint-disable react/function-component-definition */
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { parseDate } from "../../../helpers/date";
import {
  useGetAllBaseRateFilesQuery,
  useUploadBaseRateFileMutation,
  useUpdateBaseRateFileMutation,
  useDeleteBaseRateFileMutation,
  useGetSourceStatusQuery,
} from "../../../store/queries/baseRateFiles";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import { IBaseRateFile } from "../../../types/baseRateFile";
import CreateBaseRateFileDialog from "./CreateBaseRateFileDialog";
import UploadSourceFilesDialog from "./UpoadSourceFilesDialog/UploadSourceFilesDialog";

const StyledDialog = styled(Dialog)`
  .selected-files-list {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;

    .selected-file-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #f8f9fa;

      &:last-child {
        border-bottom: none;
      }

      .file-name {
        flex: 1;
        margin-right: 1rem;
      }

      .file-size {
        color: #6c757d;
        margin-right: 1rem;
        font-size: 0.875rem;
      }

      .remove-file-btn {
        background: transparent;
        border: none;
        color: #6c757d;
        width: 2rem;
        height: 2rem;
        padding: 0;

        &:hover {
          background: transparent;
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        .p-button-icon {
          font-size: 1rem;
        }
      }
    }
  }

  .file-upload-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }
`;

interface IBaseRateFilesTableProps {
  accountId?: string;
}

const BaseRateFilesTable: React.FC<IBaseRateFilesTableProps> = ({
  accountId,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [updateDialogVisible, setUpdateDialogVisible] = useState(false);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [uploadSourceFilesDialogVisible, setUploadSourceFilesDialogVisible] =
    useState(false);
  const [selectedFileForUpdate, setSelectedFileForUpdate] =
    useState<IBaseRateFile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const { data: baseRateFilesData, isLoading } = useGetAllBaseRateFilesQuery({
    search: searchValue,
    accountId,
  });
  const {
    data: sourceStatusData,
    isLoading: isSourceStatusLoading,
    refetch: refetchSourceStatus,
  } = useGetSourceStatusQuery();
  const [uploadFile] = useUploadBaseRateFileMutation();
  const [updateFile] = useUpdateBaseRateFileMutation();
  const [deleteFile] = useDeleteBaseRateFileMutation();

  const baseRateFiles = baseRateFilesData?.data || [];

  const handleUpload = async () => {
    if (!displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    setIsUploading(true);

    try {
      await uploadFile({
        file: selectedFile,
        displayName,
        accountId,
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully",
      });

      setUploadDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to upload files",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedFileForUpdate || !displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    setIsUploading(true);

    try {
      await updateFile({
        id: selectedFileForUpdate.id,
        file: selectedFile,
        displayName,
        accountId,
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail:
          "Base rate file updated successfully. Active contracts using this file have been flagged for procedure regeneration.",
        life: 5000,
      });

      setUpdateDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setSelectedFileForUpdate(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update base rate file",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openUpdateDialog = (file: IBaseRateFile) => {
    setSelectedFileForUpdate(file);
    setDisplayName(file.displayName);
    setUpdateDialogVisible(true);
  };

  const handleDelete = (file: IBaseRateFile) => {
    confirmDialog({
      message: `Are you sure you want to delete ${file.fileName}?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(file.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "File deleted successfully",
          });
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete file",
          });
        }
      },
    });
  };

  const handleDownload = async (file: IBaseRateFile) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${apiUrl.endsWith("/") ? "" : "/"}base-rate-files/${
          file.id
        }/download-url`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to get download URL",
      });
    }
  };

  const actionBodyTemplate = (rowData: IBaseRateFile) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleDownload(rowData)}
          tooltip="Download"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openUpdateDialog(rowData)}
          tooltip="Update"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const uploadDialog = (
    <StyledDialog
      header="Upload Base Rate Files"
      visible={uploadDialogVisible}
      onHide={() => {
        if (!isUploading) {
          setUploadDialogVisible(false);
          setDisplayName("");
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      }}
      style={{ width: "600px" }}
      closable={!isUploading}
      closeOnEscape={!isUploading}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="displayName">Display Name *</label>
          <InputText
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter a name for these base rate files"
          />
        </div>
        <div className="field">
          <label>Select Base Rate File (CSV) *</label>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setSelectedFile(e.target.files[0]);
                e.target.value = ""; // Reset input so same file can be selected again
              }
            }}
            style={{ display: "none" }}
            id="file-upload-input"
          />
          <Button
            label="Choose File"
            icon="pi pi-plus"
            className="p-button-outlined mb-3"
            onClick={() =>
              document.getElementById("file-upload-input")?.click()
            }
          />
          {selectedFile && (
            <>
              <div className="selected-files-list">
                <div className="selected-file-item">
                  <span className="file-name">{selectedFile.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => {
                      setSelectedFile(null);
                    }}
                  />
                </div>
              </div>
              <div className="file-upload-actions">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                />
                <Button
                  label="Upload"
                  icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                  onClick={handleUpload}
                  disabled={isUploading}
                  loading={isUploading}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </StyledDialog>
  );

  return (
    <div>
      <Toast ref={toast} />
      <div className="mb-3 flex justify-content-between">
        <div className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search base rate files..."
          />
        </div>
        <div className="flex gap-2">
          <Button
            label="Upload Source Files"
            icon="pi pi-cloud-upload"
            className="p-button-outlined"
            loading={isSourceStatusLoading}
            onClick={() => setUploadSourceFilesDialogVisible(true)}
          />
          <Button
            label="Create File"
            icon="pi pi-plus"
            className="p-button-outlined"
            onClick={() => setCreateDialogVisible(true)}
          />
          <Button
            label="Upload Files"
            icon="pi pi-upload"
            onClick={() => setUploadDialogVisible(true)}
          />
        </div>
      </div>
      <DataTable
        value={baseRateFiles || []}
        loading={isLoading}
        paginator
        rows={10}
        emptyMessage="No files uploaded yet."
        className="p-datatable-striped"
      >
        <Column field="displayName" header="Display Name" sortable />
        <Column field="fileName" header="File Name" sortable />
        <Column
          field="uploadedBy.name"
          header="Uploaded By"
          body={(rowData) => rowData.uploadedBy?.name}
        />
        <Column
          field="createdAt"
          header="Date Uploaded"
          sortable
          body={(rowData) => parseDate(rowData.createdAt)}
        />
        <Column
          header="Procedures"
          body={(rowData) => {
            const rowCount = rowData.metadata?.rowCount;
            return rowCount !== undefined
              ? `${rowCount.toLocaleString()} rows`
              : "-";
          }}
        />
        <Column field="version" header="Version" sortable />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "150px" }}
        />
      </DataTable>
      {uploadDialog}
      <StyledDialog
        header="Update Base Rate File"
        visible={updateDialogVisible}
        onHide={() => {
          if (!isUploading) {
            setUpdateDialogVisible(false);
            setDisplayName("");
            setSelectedFile(null);
            setSelectedFileForUpdate(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        }}
        style={{ width: "650px" }}
        closable={!isUploading}
        closeOnEscape={!isUploading}
      >
        <div className="p-fluid">
          {/* File Information Header */}
          <div className="mb-4 p-3 surface-100 border-round">
            <h6 className="mt-0 mb-2 text-900">
              {selectedFileForUpdate?.displayName}
            </h6>
            <div className="flex align-items-center gap-3 text-600">
              <span>
                <i className="pi pi-file mr-1" />
                {selectedFileForUpdate?.fileName}
              </span>
              <span>
                <i className="pi pi-tag mr-1" />
                Version {selectedFileForUpdate?.version}
              </span>
            </div>
          </div>

          {/* Display Name Field */}
          <div className="field">
            <label htmlFor="updateDisplayName" className="font-medium">
              Display Name *
            </label>
            <InputText
              id="updateDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a display name for this base rate file"
              className="w-full"
            />
          </div>

          {/* Version Information */}
          <div className="field">
            <div className="p-3 surface-50 border-1 border-200 border-round">
              <div className="flex align-items-center justify-content-between mb-2">
                <span className="font-medium text-900">Version Update</span>
                <span className="text-600">
                  {selectedFileForUpdate?.version} →{" "}
                  {(selectedFileForUpdate?.version || 0) + 1}
                </span>
              </div>
              <p className="text-sm text-600 m-0">
                This will create a new version while preserving the existing
                version for historical reference.
              </p>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="field">
            <label className="font-medium">New Base Rate File (CSV) *</label>

            {!selectedFile ? (
              <div
                className="p-4 border-2 border-dashed border-300 border-round text-center cursor-pointer hover:border-400 transition-colors"
                onClick={() =>
                  document.getElementById("file-update-input")?.click()
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById("file-update-input")?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Click to select a new CSV file"
              >
                <i className="pi pi-cloud-upload text-4xl text-400 mb-3" />
                <p className="text-600 mb-2">Click to select a new CSV file</p>
                <p className="text-sm text-500">or drag and drop here</p>
              </div>
            ) : (
              <div className="selected-files-list">
                <div className="selected-file-item bg-primary-50 border-primary-200">
                  <i className="pi pi-file text-primary mr-2" />
                  <span className="file-name text-primary font-medium flex-1">
                    {selectedFile.name}
                  </span>
                  <span className="text-sm text-600 mr-2">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => setSelectedFile(null)}
                  />
                </div>
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                  e.target.value = "";
                }
              }}
              style={{ display: "none" }}
              id="file-update-input"
            />
          </div>

          {/* Warning Message */}
          <div className="p-3 border-left-3 border-orange-500 surface-ground">
            <div className="flex align-items-start">
              <i className="pi pi-exclamation-triangle text-orange-500 mr-2 mt-1" />
              <div>
                <h6 className="mt-0 mb-2 text-900">Important Notice</h6>
                <p className="text-sm text-700 m-0 line-height-3">
                  Updating this base rate file will automatically flag all
                  active contracts that reference it as outdated. You&apos;ll
                  need to manually regenerate provider procedures for those
                  contracts to apply the new rates.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setUpdateDialogVisible(false);
                setSelectedFile(null);
                setSelectedFileForUpdate(null);
                setDisplayName("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              disabled={isUploading}
            />
            <Button
              label={isUploading ? "Updating..." : "Update File"}
              icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
              onClick={handleUpdate}
              disabled={isUploading || !selectedFile || !displayName.trim()}
              loading={isUploading}
            />
          </div>
        </div>
      </StyledDialog>
      <CreateBaseRateFileDialog
        visible={createDialogVisible}
        onHide={() => setCreateDialogVisible(false)}
        sourceDataStatus={sourceStatusData?.data ?? null}
      />
      <UploadSourceFilesDialog
        visible={uploadSourceFilesDialogVisible}
        onHide={() => setUploadSourceFilesDialogVisible(false)}
        sourceDataStatus={sourceStatusData?.data ?? null}
        onSuccess={() => {
          refetchSourceStatus();
        }}
      />
    </div>
  );
};

export default BaseRateFilesTable;
